import { useMemo, useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  IconButton,
  Tooltip,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ClearAllIcon from "@mui/icons-material/ClearAll";

export default function PermissionsPicker({
  options = [],
  value = [],
  onChange,
  error,
  helperText,
  height = 320,
}) {
  const [qLeft, setQLeft] = useState("");
  const [qRight, setQRight] = useState("");

  const selectedIds = useMemo(() => new Set(value.map((p) => p.id)), [value]);

  const available = useMemo(
    () => options.filter((p) => !selectedIds.has(p.id)),
    [options, selectedIds],
  );

  const leftFiltered = useMemo(() => {
    const q = qLeft.trim().toLowerCase();
    if (!q) return available;
    return available.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [available, qLeft]);

  const rightFiltered = useMemo(() => {
    const q = qRight.trim().toLowerCase();
    if (!q) return value;
    return value.filter((p) => (p.name || "").toLowerCase().includes(q));
  }, [value, qRight]);

  const addOne = (perm) => onChange?.([...value, perm]);
  const removeOne = (perm) => onChange?.(value.filter((p) => p.id !== perm.id));

  const addAllFiltered = () => {
    const toAdd = leftFiltered;
    if (!toAdd.length) return;
    onChange?.([...value, ...toAdd]);
  };

  const removeAllFiltered = () => {
    const ids = new Set(rightFiltered.map((p) => p.id));
    onChange?.(value.filter((p) => !ids.has(p.id)));
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <Typography sx={{ fontWeight: 800 }}>Permisos</Typography>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          {value.length} seleccionados
        </Typography>
      </Box>

      <Box
        sx={{
          mt: 1,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 1.5,
        }}
      >
        {/* Disponibles */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            borderColor: error ? "rgba(211,47,47,0.55)" : "rgba(0,0,0,0.12)",
          }}
        >
          <Box sx={{ p: 1.2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 13 }}>Disponibles</Typography>

              <Tooltip title="Agregar todos los filtrados">
                <span>
                  <IconButton size="small" onClick={addAllFiltered} disabled={!leftFiltered.length}>
                    <DoneAllIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <TextField
              size="small"
              placeholder="Buscar permiso..."
              value={qLeft}
              onChange={(e) => setQLeft(e.target.value)}
              fullWidth
            />
          </Box>

          <Divider />

          <Box sx={{ height, overflow: "auto", p: 0.8 }}>
            {leftFiltered.map((perm) => (
              <Box
                key={perm.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1,
                  py: 0.9,
                  borderRadius: 2,
                  cursor: "pointer",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                }}
                onClick={() => addOne(perm)}
              >
                <Typography sx={{ fontSize: 13.5 }}>{perm.name}</Typography>
                <IconButton size="small" onClick={() => addOne(perm)}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            {!leftFiltered.length && (
              <Typography variant="body2" sx={{ opacity: 0.7, p: 1 }}>
                No hay permisos para mostrar.
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Seleccionados */}
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            borderColor: error ? "rgba(211,47,47,0.55)" : "rgba(0,0,0,0.12)",
          }}
        >
          <Box sx={{ p: 1.2 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 13 }}>Seleccionados</Typography>

              <Tooltip title="Quitar todos los filtrados">
                <span>
                  <IconButton size="small" onClick={removeAllFiltered} disabled={!rightFiltered.length}>
                    <ClearAllIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <TextField
              size="small"
              placeholder="Buscar en seleccionados..."
              value={qRight}
              onChange={(e) => setQRight(e.target.value)}
              fullWidth
            />
          </Box>

          <Divider />

          <Box sx={{ height, overflow: "auto", p: 0.8 }}>
            {rightFiltered.map((perm) => (
              <Box
                key={perm.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1,
                  py: 0.9,
                  borderRadius: 2,
                  cursor: "pointer",
                  bgcolor: "rgba(25,118,210,0.06)",
                  "&:hover": { bgcolor: "rgba(25,118,210,0.10)" },
                }}
                onClick={() => removeOne(perm)}
              >
                <Typography sx={{ fontSize: 13.5 }}>{perm.name}</Typography>
                <IconButton size="small" onClick={() => removeOne(perm)}>
                  <RemoveIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            {!rightFiltered.length && (
              <Typography variant="body2" sx={{ opacity: 0.7, p: 1 }}>
                Todavía no seleccionaste permisos.
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>

      {helperText && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.8, color: error ? "error.main" : "text.secondary" }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
}
